import { Helmet } from 'react-helmet';
import QuizLayout from '@components/layouts/quiz-layout';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Button from '@ui/Button';
import { ButtonTypes } from '@ui/Button/button-types';
import Input from '@ui/Input';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { CepResponse, CitiesResponse } from '@utils/api/quiz';
import Select from '@ui/Select';
import Tooltip from '@ui/Tooltip';
import { connect } from 'react-redux';
import { getCityByState, registerQuizUser } from '@redux/actions';
import { createSelector } from 'reselect';
import {
  addressQuizSelector,
  isLoadingRegisterQuizUserSelector
} from '@redux/selectors';
import { add } from 'date-fns';
import Media from 'react-responsive';
import './quiz.css';
import CircleQuestionMark from '@assets/icons/circle-question-mark';
import { zodResolver } from '@hookform/resolvers/zod';
import { MAX_MOBILE_WIDTH } from '../../../../config/misc';
import { QuizUser, QuizUserStatus } from './types';
import { quizSchema, QuizSchema } from './quiz.schema';

type SelectOption = {
  value: string;
  option: string;
};

type FormData = {
  name: string;
  phone: string;
  email: string;
  cpf: string;
  state: string;
  city: string;
};

type AddresQuiz = {
  loading: boolean;
  address: CepResponse;
  cities: CitiesResponse[];
};

type QuizPageProps = {
  readonly isLoadingRegisterQuizUser: boolean;
  readonly registerQuizUser: (data: QuizUser) => void;
  readonly getCityByState: (state: string) => void;
  readonly addressQuiz: AddresQuiz;
};

const Container = styled('div')`
  background: #25132d;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
`;

const LeftCircle = styled('div')`
  width: 494px;
  height: 494px;
  position: absolute;
  left: -271px;
  top: -51px;
  border-radius: 494px;
  background: rgba(119, 53, 149, 0.5);
  filter: blur(82.75px);
  pointer-events: none;
`;

const RightCircle = styled('div')`
  width: 484px;
  height: 484px;
  position: absolute;
  right: -353px;
  bottom: -163px;
  border-radius: 484px;
  background: rgba(242, 0, 130, 0.5);
  filter: blur(82.75px);
  pointer-events: none;
`;

const MainContent = styled('div')`
  max-width: 960px;
  display: flex;
  flex-direction: row;
  gap: 4rem;
  padding: 2rem;

  @media (max-width: 960px) {
    width: 100%;
    flex-direction: column;
    gap: 1.5rem;
    align-items: center;
  }
`;

const TextContent = styled('div')`
  max-width: 469px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  justify-content: center;

  @media (max-width: 960px) {
    gap: 1rem;
    padding: 0rem;
    max-width: 427px;
  }
`;

const MainTitle = styled('h1')`
  color: var(--System-White, #fff);
  font-size: 52px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -1.04px;
  margin: 0;

  @media (max-width: 960px) {
    font-size: 24px;
    line-height: 120%;
  }
`;

const Highlight = styled('span')`
  color: var(--Pallets-Primary-800, #f081b8);
`;

const Subtitle = styled('p')`
  color: var(--System-White, #fff);
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;

  @media (max-width: 960px) {
    font-size: 16px;
    line-height: 150%;
  }
`;

const MainForm = styled('form')`
  max-width: 427px;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const TermsOfUse = styled('p')`
  color: var(--Transparence-White-80, rgba(255, 255, 255, 0.8));
  text-align: center;
  font-family: 'Poppins', Arial, Helvetica, sans-serif;

  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 18px */
`;

const initialFormValues = {
  name: '',
  phone: '',
  email: '',
  cpf: '',
  state: '',
  city: ''
};

export const STATE_OPTIONS = [
  { uf: 'AC', state: 'Acre' },
  { uf: 'AL', state: 'Alagoas' },
  { uf: 'AM', state: 'Amazonas' },
  { uf: 'AP', state: 'Amapá' },
  { uf: 'BA', state: 'Bahia' },
  { uf: 'CE', state: 'Ceará' },
  { uf: 'DF', state: 'Distrito Federal' },
  { uf: 'ES', state: 'Espírito Santo' },
  { uf: 'GO', state: 'Goiás' },
  { uf: 'MA', state: 'Maranhão' },
  { uf: 'MG', state: 'Minas Gerais' },
  { uf: 'MS', state: 'Mato Grosso do Sul' },
  { uf: 'MT', state: 'Mato Grosso' },
  { uf: 'PA', state: 'Pará' },
  { uf: 'PB', state: 'Paraíba' },
  { uf: 'PE', state: 'Pernambuco' },
  { uf: 'PI', state: 'Piauí' },
  { uf: 'PR', state: 'Paraná' },
  { uf: 'RJ', state: 'Rio de Janeiro' },
  { uf: 'RN', state: 'Rio Grande do Norte' },
  { uf: 'RO', state: 'Rondônia' },
  { uf: 'RR', state: 'Roraima' },
  { uf: 'RS', state: 'Rio Grande do Sul' },
  { uf: 'SC', state: 'Santa Catarina' },
  { uf: 'SE', state: 'Sergipe' },
  { uf: 'SP', state: 'São Paulo' },
  { uf: 'TO', state: 'Tocantins' }
];

const mapStateToProps = createSelector(
  isLoadingRegisterQuizUserSelector,
  addressQuizSelector,
  (isLoadingRegisterQuizUser: boolean, addressQuiz: AddresQuiz) => ({
    isLoadingRegisterQuizUser,
    addressQuiz
  })
);

const mapDispatchToProps = {
  registerQuizUser,
  getCityByState
};

const resolver = zodResolver(quizSchema) as Resolver<QuizSchema>;

function QuizPage({
  isLoadingRegisterQuizUser,
  registerQuizUser,
  addressQuiz,
  getCityByState
}: QuizPageProps): JSX.Element {
  const [cities, setCities] = useState<SelectOption[]>([]);
  const [disableState, setDisableState] = useState(false);
  const {
    register,
    setValue,
    control,
    formState: { errors },
    handleSubmit,
    watch
  } = useForm<QuizSchema>({ resolver, defaultValues: initialFormValues });

  const stateValue = watch('state');
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const state = urlParams.get('state');
    const isValidState = STATE_OPTIONS.some(
      option => option.uf.toUpperCase() === state?.toUpperCase()
    );

    if (state && isValidState) {
      setValue('state', state.toUpperCase());
      setDisableState(true);
    } else {
      setValue('state', '');
      setDisableState(false);
      setCities([]);
    }
  }, [setValue]);

  const onSubmit = (data: FormData) => {
    const now = new Date();
    const reminderDate = add(now, { hours: 24 });

    const newQuizUser: QuizUser = {
      ...data,
      status: QuizUserStatus.PENDING,
      registrationQuiz: null,
      quizCompletionDate: null,
      isReminderSent: false,
      reminderDate,
      migrationDate: null
    };

    registerQuizUser(newQuizUser);
  };

  useEffect(() => {
    if (stateValue) {
      getCityByState(stateValue);
      setValue('city', '');
    }
  }, [stateValue, getCityByState, setValue]);

  useEffect(() => {
    if (addressQuiz.cities && addressQuiz.cities.length > 0) {
      const formattedCities = addressQuiz.cities.map(city => ({
        value: city.nome,
        option: city.nome
      }));
      setCities(formattedCities);
    } else {
      setCities([]);
    }
  }, [addressQuiz.cities, setCities]);

  const emailTooltipMessage =
    'O e-mail que você cadastrar aqui, será o mesmo para as próximas etapas da Jornada Dev SENAI.';

  return (
    <QuizLayout>
      <Helmet>
        <title>DEVstart - Quiz</title>
      </Helmet>
      <Container>
        <LeftCircle />
        <RightCircle />

        <MainContent>
          <TextContent>
            <MainTitle>
              Faça o teste e inicie sua{' '}
              <Highlight>Jornada DEV SENAI!</Highlight>
            </MainTitle>
            <Subtitle>
              Aprenda programação front-end gratuitamente, com certificados e
              qualidade de ensino SENAI.
            </Subtitle>
          </TextContent>

          <MainForm onSubmit={handleSubmit(onSubmit)}>
            <Input
              type='text'
              placeholder='Nome'
              hasError={errors.name?.message}
              messageError={errors.name?.message}
              error={errors.name?.message}
              inputStyleType='type-2'
              {...register('name')}
            />
            <Input
              type='text'
              placeholder='Telefone'
              hasError={errors.phone?.message}
              messageError={errors.phone?.message}
              inputStyleType='type-2'
              hasMask
              maskValue='(99) 99999-9999'
              {...register('phone')}
            />
            <Media maxWidth={MAX_MOBILE_WIDTH}>
              <Input
                type='text'
                placeholder='E-mail'
                hasError={errors.email?.message}
                messageError={errors.email?.message}
                inputStyleType='type-2'
                insideIcon={
                  <Tooltip title={emailTooltipMessage}>
                    <span className='circle-question-mark-container'>
                      <CircleQuestionMark />
                    </span>
                  </Tooltip>
                }
                {...register('email')}
              />
            </Media>
            <Media minWidth={MAX_MOBILE_WIDTH + 1}>
              <Tooltip title={emailTooltipMessage}>
                <Input
                  type='text'
                  placeholder='E-mail'
                  hasError={errors.email?.message}
                  messageError={errors.email?.message}
                  inputStyleType='type-2'
                  {...register('email')}
                />
              </Tooltip>
            </Media>
            <Input
              type='text'
              placeholder='CPF'
              hasError={errors.cpf?.message}
              messageError={errors.cpf?.message}
              inputStyleType='type-2'
              hasMask
              maskValue='999.999.999-99'
              {...register('cpf')}
            />
            <Controller
              control={control}
              name='state'
              defaultValue=''
              render={({ field: { value, onChange } }) => (
                <Select
                  placeholder='Estado'
                  options={STATE_OPTIONS.map(state => ({
                    value: state.uf,
                    option: state.state
                  }))}
                  error={errors.state?.message}
                  selectStyleType='type-2'
                  disabled={disableState}
                  value={value}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                    onChange(e.target.value);
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name='city'
              defaultValue=''
              render={({ field: { value, onChange } }) => (
                <Select
                  placeholder='Cidade'
                  options={cities}
                  error={errors.city?.message}
                  selectStyleType='type-2'
                  value={value}
                  onChange={onChange}
                  disabled={cities.length === 0}
                  isLoading={addressQuiz.loading}
                />
              )}
            />
            <Button
              buttonType={ButtonTypes.Primary}
              style={{
                marginTop: 12,
                marginBottom: 12,
                width: '100%'
              }}
              type='submit'
              isLoading={isLoadingRegisterQuizUser}
            >
              COMEÇAR O TESTE
            </Button>

            <TermsOfUse>
              Ao começar o teste, você concorda com os Termos de Uso e a
              Política de Privacidade do DEVstart.
            </TermsOfUse>
          </MainForm>
        </MainContent>
      </Container>
    </QuizLayout>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(QuizPage);
